import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './App.css';
import { Container, Row, Col, Spinner, Button } from 'react-bootstrap';
import { Field, Formik, Form as FormikForm } from 'formik';
import * as yup from 'yup';
import 'yup-phone-lite';
import FormInput from './components/inputBox';
import FormSelect from './components/formSelect';
import { PackageInfo } from './packageInfo';
import { formatPrice, luhnCheck, formatDate } from './util';
import { config } from './config';

const validationSchema = yup.object().shape({
  cardNumber: yup
    .string()
    .required('Card number is required')
    .matches(/^[0-9]+$/, 'Card number must only contain digits')
    .min(13, 'Card number must be at least 13 digits')
    .max(19, 'Card number must not exceed 19 digits')
    .test('luhn', 'Invalid card number', (value: any) => {
      if (!value) return true; // Let required() handle empty values
      return luhnCheck(value);
    }),
  expDateM: yup.string().required('Exp. Month is required'),
  expDateY: yup.string().required('Exp. Year is required'),
  cvv: yup.string().required('CVV is required'),
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  address: yup.string().required('Address is required'),
  city: yup.string().required('City is required'),
  state: yup.string().required('State is required'),
  zip: yup.string().required('Zip is required'),
  email: yup.string().email().required('Email is required'),
  phone: yup.string().phone('US', 'Please enter a valid phone number').required('Phone is required'),
  country: yup.string().required('Country is required'),
  terms: yup.boolean().oneOf([true], 'You must accept the terms and conditions').required(),
});

const initialValues = {
  cardNumber: '',
  expDateM: '',
  expDateY: '',
  cvv: '',
  firstName: '',
  lastName: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  email: '',
  phone: '',
  country: '',
  terms: false,
};

const App = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);

  const { packageType } = useParams<{ packageType?: string }>();

  const [selectedPackage, setSelectedPackage] = useState<PackageInfo | undefined>(undefined);

  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const fetchData = async () => {
      if (!packageType) return;
      setIsLoading(true);

      try {
        const response = await fetch(`${config.paymentItemsUrl}/${packageType}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setSelectedPackage(data);
        } else {
          console.error('Failed to fetch data');
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [packageType]);

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      const response = await fetch(config.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...values, amount: selectedPackage?.price, packageId: selectedPackage?.id }),
      });
      if (response.ok) {
        setIsCompleted(true);
      } else {
        alert('Failed to submit payment');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to submit payment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div id="wrap">
      <header>
        <section className="container-fluid container-xl">
          <div className="row">
            <div className="col-12 col-md-5">
              <img alt="Collector Car Leaders logo" id="logo" src="/images/ccl-logo-2025-white.png" />
            </div>
            <div className="col-12 col-md-7 text-center text-md-right flex">
              <div>
                <a className="bttn mr-2" title="Call Collector Car Leaders" href="tel:800-224-8180">
                  Call Us
                </a>
                <a
                  className="bttn"
                  title="Collector Car Leaders website"
                  href="https://www.collectorcarleaders.com"
                  target="_blank"
                  rel="noreferrer"
                  data-discover="true"
                >
                  Homepage
                </a>
              </div>
            </div>
          </div>
        </section>
      </header>

      <section id="body-content">
        {isCompleted ? (
          <div
            className="container"
            style={{
              minHeight: '80vh',
              width: '100vw',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div className="row">
              <div className="col-12 text-center">
                <h1>Thank you for your payment!</h1>
                <p>Your transaction has been processed successfully.</p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {isLoading ? (
              <div
                className="container"
                style={{
                  minHeight: '80vh',
                  width: '100vw',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div className="row">
                  <div className="col-12 text-center">
                    <Spinner animation="border" variant="primary" />
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="py-4">
                  <h1 className="mb-3">
                    Hi {selectedPackage?.firstName} {selectedPackage?.lastName},
                  </h1>
                  <p>{selectedPackage?.description}</p>

                  <div className="p-2">
                    {selectedPackage?.items.map((item, index) => (
                      <article className="service-item" key={index}>
                        <h5>
                          {item.name} - {formatPrice(item.price)}
                        </h5>
                        <p>{item.description}</p>
                      </article>
                    ))}

                    <div className="pt-4 px-3">
                      <h2 className="blue">
                        <strong>Total: {formatPrice(selectedPackage?.price || 0)}</strong>
                      </h2>
                      <p>
                        <strong>Due {selectedPackage?.dueDate ? formatDate(selectedPackage.dueDate) : ''}</strong>
                      </p>
                    </div>
                  </div>
                </div>

                <hr className="mb-4" />

                <div>
                  <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                    {({ isSubmitting }) => (
                      <FormikForm id="pay-form">
                        <Container fluid="xl">
                          <Row>
                            <Col xs={12}>
                              <h3>Payment Information</h3>
                            </Col>
                            <Col xs={12}>
                              <FormInput label="Card Number:" name="cardNumber" type="text" />
                            </Col>
                            <Col sm={4}>
                              <FormSelect
                                label="Exp. Month"
                                name="expDateM"
                                options={[
                                  { value: '', label: 'Month' },
                                  ...[...Array(12)].map((_, i) => ({
                                    value: `${i + 1}`.padStart(2, '0'),
                                    label: `${i + 1}`.padStart(2, '0'),
                                  })),
                                ]}
                              />
                            </Col>
                            <Col sm={4}>
                              <FormSelect
                                label="Exp. Year"
                                name="expDateY"
                                options={[
                                  { value: '', label: 'Year' },
                                  ...[...Array(14)].map((_, i) => ({
                                    value: `${currentYear + i}`,
                                    label: `${currentYear + i}`,
                                  })),
                                ]}
                              />
                            </Col>
                            <Col sm={4}>
                              <FormInput label="CVV:" name="cvv" type="text" />
                            </Col>

                            <Col xs={12} className="mt-5">
                              <h3>Billing Information</h3>
                            </Col>
                            <Col sm={6}>
                              <FormInput label="First Name:" name="firstName" type="text" />
                            </Col>
                            <Col sm={6}>
                              <FormInput label="Last Name:" name="lastName" type="text" />
                            </Col>
                            <Col xs={12}>
                              <FormInput label="Street Address:" name="address" type="text" />
                            </Col>
                            <Col md={6}>
                              <FormInput label="City:" name="city" type="text" />
                            </Col>
                            <Col md={3} xs={6}>
                              <FormSelect
                                label="State:"
                                name="state"
                                options={[
                                  { value: '', label: 'Choose' },
                                  { value: 'AL', label: 'Alabama' },
                                  { value: 'AK', label: 'Alaska' },
                                  { value: 'AZ', label: 'Arizona' },
                                  { value: 'AR', label: 'Arkansas' },
                                  { value: 'CA', label: 'California' },
                                  { value: 'CO', label: 'Colorado' },
                                  { value: 'CT', label: 'Connecticut' },
                                  { value: 'DE', label: 'Delaware' },
                                  { value: 'DC', label: 'District Of Columbia' },
                                  { value: 'FL', label: 'Florida' },
                                  { value: 'GA', label: 'Georgia' },
                                  { value: 'HI', label: 'Hawaii' },
                                  { value: 'ID', label: 'Idaho' },
                                  { value: 'IL', label: 'Illinois' },
                                  { value: 'IN', label: 'Indiana' },
                                  { value: 'IA', label: 'Iowa' },
                                  { value: 'KS', label: 'Kansas' },
                                  { value: 'KY', label: 'Kentucky' },
                                  { value: 'LA', label: 'Louisiana' },
                                  { value: 'ME', label: 'Maine' },
                                  { value: 'MD', label: 'Maryland' },
                                  { value: 'MA', label: 'Massachusetts' },
                                  { value: 'MI', label: 'Michigan' },
                                  { value: 'MN', label: 'Minnesota' },
                                  { value: 'MS', label: 'Mississippi' },
                                  { value: 'MO', label: 'Missouri' },
                                  { value: 'MT', label: 'Montana' },
                                  { value: 'NE', label: 'Nebraska' },
                                  { value: 'NV', label: 'Nevada' },
                                  { value: 'NH', label: 'New Hampshire' },
                                  { value: 'NJ', label: 'New Jersey' },
                                  { value: 'NM', label: 'New Mexico' },
                                  { value: 'NY', label: 'New York' },
                                  { value: 'NC', label: 'North Carolina' },
                                  { value: 'ND', label: 'North Dakota' },
                                  { value: 'OH', label: 'Ohio' },
                                  { value: 'OK', label: 'Oklahoma' },
                                  { value: 'OR', label: 'Oregon' },
                                  { value: 'PA', label: 'Pennsylvania' },
                                  { value: 'RI', label: 'Rhode Island' },
                                  { value: 'SC', label: 'South Carolina' },
                                  { value: 'SD', label: 'South Dakota' },
                                  { value: 'TN', label: 'Tennessee' },
                                  { value: 'TX', label: 'Texas' },
                                  { value: 'UT', label: 'Utah' },
                                  { value: 'VT', label: 'Vermont' },
                                  { value: 'VA', label: 'Virginia' },
                                  { value: 'WA', label: 'Washington' },
                                  { value: 'WV', label: 'West Virginia' },
                                  { value: 'WI', label: 'Wisconsin' },
                                  { value: 'WY', label: 'Wyoming' },
                                ]}
                              />
                            </Col>
                            <Col md={3} xs={6}>
                              <FormInput label="Zip:" name="zip" type="text" />
                            </Col>
                            <Col md={6}>
                              <FormInput label="Email:" name="email" type="email" />
                            </Col>
                            <Col md={6}>
                              <FormInput label="Phone:" name="phone" type="text" />
                            </Col>
                            <Col xs={12}>
                              <FormSelect
                                label="Country:"
                                name="country"
                                options={[
                                  { value: '', label: 'Choose' },
                                  { value: 'USA', label: 'USA' },
                                  { value: 'Canada', label: 'Canada' },
                                  { value: 'Other', label: 'Other' },
                                ]}
                              />
                            </Col>

                            <div className="p-4">
                              <p>
                                <Field name="terms">
                                  {({ field, meta }: { field: any; meta: any }) => (
                                    <div>
                                      <label>
                                        <input type="checkbox" {...field} /> I authorize Trailer Solutions – FL, DBA:
                                        Collector Car Leaders (CCL), to charge the credit card provided through this
                                        online payment portal in accordance with the terms outlined at the time of
                                        purchase. This authorization applies solely to the goods and/or services
                                        described and for the specified amount, and is valid for a one-time transaction
                                        only. I certify that I am an authorized user of this credit card and that I will
                                        not dispute this charge with my credit card issuer, provided the transaction is
                                        processed in accordance with the stated terms. By completing this payment, I
                                        acknowledge that I have read, understand, and agree to the{' '}
                                        <a
                                          href="https://www.collectorcarleaders.com/privacy"
                                          title="Privacy Policy"
                                          target="_blank"
                                          rel="nofollow noreferrer"
                                        >
                                          Privacy Policy
                                        </a>{' '}
                                        and{' '}
                                        <a
                                          href="https://www.collectorcarleaders.com/terms-of-use"
                                          title="Terms of Use"
                                          target="_blank"
                                          rel="nofollow noreferrer"
                                        >
                                          Terms of Use
                                        </a>
                                        .
                                      </label>
                                      {meta.touched && meta.error && <div className="text-danger">{meta.error}</div>}
                                    </div>
                                  )}
                                </Field>
                              </p>
                            </div>

                            <Col xs={12} className="mt-3 text-center">
                              <Button type="submit" className="bttn bttn-blue" id="btnSubmit" disabled={isSubmitting}>
                                {isSubmitting ? <i className="fas fa-spinner fa-spin" id="ajax-loader"></i> : null}
                                Pay Now
                              </Button>
                            </Col>
                          </Row>
                        </Container>
                      </FormikForm>
                    )}
                  </Formik>
                </div>
              </>
            )}
          </>
        )}
      </section>

      <div className="slant-top"></div>

      <footer>
        <section className="container-fluid container-xl">
          <div className="row">
            <div className="col-12 text-center" id="disclaimer">
              <p>
                &copy; Copyright {currentYear} Collector Car Leaders. All Rights Reserved.
                <br />
                <a
                  title="Privacy Policy"
                  href="https://www.collectorcarleaders.com/privacy"
                  data-discover="true"
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>
                &nbsp;&nbsp;
                <a
                  title="Terms of Use"
                  href="https://www.collectorcarleaders.com/beta/terms-of-use"
                  data-discover="true"
                  target="_blank"
                  rel="noreferrer"
                >
                  Terms of Use
                </a>
              </p>
            </div>
          </div>
        </section>
      </footer>
    </div>
  );
};

export default App;
